import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { VscChevronDown, VscChevronUp } from 'react-icons/vsc'

import {
  AccordionContainer,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
} from './ObjetivesSection/styles'
import { Button, InlineMenu, SectionSubtitle, Textarea } from '../../../..'
import {
  IMPROVISACION_ROUTINE_KEY,
  TRAINING_SUBFOLDER_SLUGS,
  TRAINING_TYPE_LABELS,
  VOICE_CATEGORY_LABELS,
  VOICE_CATEGORY_SLUGS,
  VoiceCategorySlug,
} from '../../../../../constants/tracks'
import { useStorage, useUser } from '../../../../../context'
import { TracksAudioIndex } from '../../../../../firebase/utils/storageActions'
import { LogExercices, NewLog } from '../../../../../types'
import { exerciseUsesLegacyTrackPicker } from '../../../../../utilFunctions/trackExerciseSchema'
import { SelectComponent } from '../../../../Select/Select'

import { ItemContainer } from './styles'

function resolveNewTrackList(
  index: TracksAudioIndex | null,
  ex: LogExercices,
): { title: string; url: string }[] | null {
  if (!index) return null
  const voice = ex.category.find((c) =>
    (VOICE_CATEGORY_SLUGS as readonly string[]).includes(c),
  ) as VoiceCategorySlug | undefined
  if (!voice) return null
  if (voice === 'improvisacion') {
    return index.improvisacion.length > 0 ? index.improvisacion : null
  }
  const r = ex.routine
  if (!r) return null
  const side = voice === 'chicas' ? index.chicas : index.chicos
  const list = side[r] ?? []
  return list.length > 0 ? list : null
}

const ExerciseAccordion = ({
  data,
  setLogData,
}: {
  data: NewLog
  setLogData: Dispatch<SetStateAction<NewLog | null>>
}) => {
  const { onEditionMode } = useUser()
  const { audiosLists, tracksAudioIndex } = useStorage()
  const [activeList, setActiveList] = useState<
    { title: string; url: string }[] | null
  >(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const handleToggle = useCallback(
    (index: number) => {
      setOpenIndex(openIndex === index ? null : index)
    },
    [openIndex],
  )

  const handleUpdateLegacy = useCallback(
    (value: string | string[] | null, name: keyof LogExercices) => {
      if (openIndex === null) return

      const updatedEx = data.exercises.map((exc, i) =>
        i === openIndex
          ? {
              ...exc,
              [name]: value,
              ...(name !== 'category' && {
                exercise: { title: '', url: '' },
              }),
            }
          : exc,
      )

      setLogData({ ...data, exercises: updatedEx })
    },
    [data, openIndex, setLogData],
  )

  const setVoiceCategoryAt = useCallback(
    (rowIndex: number, slug: VoiceCategorySlug) => {
      setLogData((prev) => ({
        ...prev!,
        exercises: prev!.exercises.map((exc, i) =>
          i === rowIndex
            ? {
                ...exc,
                category: [slug],
                routine: null,
                gender: null,
                exercise: { title: '', url: '' },
              }
            : exc,
        ),
      }))
    },
    [setLogData],
  )

  const setTrainingSubfolderAt = useCallback(
    (rowIndex: number, slug: string) => {
      setLogData((prev) => ({
        ...prev!,
        exercises: prev!.exercises.map((exc, i) =>
          i === rowIndex
            ? {
                ...exc,
                routine: slug,
                exercise: { title: '', url: '' },
              }
            : exc,
        ),
      }))
    },
    [setLogData],
  )

  const handleChange = useCallback(
    (
      e:
        | ChangeEvent<HTMLSelectElement>
        | ChangeEvent<HTMLInputElement>
        | ChangeEvent<HTMLTextAreaElement>,
    ) => {
      const { value, name } = e.target
      if (openIndex === null) return

      const processedValue =
        name === 'exercise'
          ? (() => {
              const [title, url] = value.split(' | ')
              return { title, url }
            })()
          : value

      setLogData((prev) => ({
        ...prev!,
        exercises: prev!.exercises.map((exc, i) => {
          if (i !== openIndex) return exc
          let next: LogExercices = {
            ...exc,
            [name]: processedValue as never,
          }
          if (
            name === 'exercise' &&
            processedValue &&
            typeof processedValue === 'object' &&
            'url' in processedValue
          ) {
            const voice = exc.category.find((c) =>
              (VOICE_CATEGORY_SLUGS as readonly string[]).includes(c),
            )
            if (voice === 'improvisacion') {
              next = { ...next, routine: IMPROVISACION_ROUTINE_KEY }
            }
          }
          return next
        }),
      }))
    },
    [openIndex, setLogData],
  )

  const filterAudioListLegacy = useCallback(
    (gender: string | null, routine: string | null) => {
      if (!audiosLists || !gender || !routine) {
        setActiveList(null)
        return
      }
      const routineIndex = {
        'Trabajo respiratorio': 1,
        'Estimulación laríngea': 0,
        'Trabajo resonancial': 2,
      }[routine]

      if (routineIndex === undefined) {
        setActiveList(null)
        return
      }

      const bucket = audiosLists[routineIndex]
      if (!bucket) {
        setActiveList(null)
        return
      }

      const list = gender === 'Masculino' ? bucket.hombre : bucket.mujer
      setActiveList(list?.length ? list : null)
    },
    [audiosLists],
  )

  useEffect(() => {
    if (openIndex === null) {
      setActiveList(null)
      return
    }
    const exercise = data.exercises[openIndex]
    if (exerciseUsesLegacyTrackPicker(exercise)) {
      filterAudioListLegacy(exercise.gender, exercise.routine)
    } else {
      setActiveList(resolveNewTrackList(tracksAudioIndex, exercise))
    }
  }, [
    openIndex,
    data.exercises,
    audiosLists,
    tracksAudioIndex,
    filterAudioListLegacy,
  ])

  const rowDisabled = (i: number) => !onEditionMode || openIndex !== i

  return (
    <AccordionContainer>
      {data.exercises.map((exer, i) => {
        const legacy = exerciseUsesLegacyTrackPicker(exer)
        const voiceSlug = exer.category.find((c) =>
          (VOICE_CATEGORY_SLUGS as readonly string[]).includes(c),
        ) as VoiceCategorySlug | undefined

        return (
          <AccordionItem key={i}>
            <AccordionHeader
              onClick={() => handleToggle(i)}
              style={{
                border: '0.5px solid #8e92bc',
                boxShadow: '0px 4px 8px 0px rgba(94, 97, 127, 0.05)',
              }}
            >
              <div style={{ display: 'flex', gap: '36px' }}>
                <p>N {i + 1}</p>
                <p>Ejercicio</p>
              </div>
              {openIndex === i ? <VscChevronUp /> : <VscChevronDown />}
            </AccordionHeader>
            <AccordionContent $isOpen={openIndex === i} style={{ gap: '24px' }}>
              {onEditionMode && (
                <SectionSubtitle
                  fontSize={14}
                  fontWeight={500}
                  classSub="section-title"
                >
                  <>
                    <span>1</span>
                    Seleccionar ejercicio
                  </>
                </SectionSubtitle>
              )}
              {legacy ? (
                <>
                  <ItemContainer>
                    <label>Categoría del ejercicio (bitácora anterior)</label>
                    <InlineMenu
                      menuName="trainingType"
                      activeFilter={exer.category}
                      setActiveFilter={(value) =>
                        handleUpdateLegacy(value, 'category')
                      }
                      isDisabled={rowDisabled(i)}
                    />
                  </ItemContainer>
                  <ItemContainer>
                    <label>Género del ejercicio</label>
                    <InlineMenu
                      menuName="exerciseGenre"
                      activeFilter={exer.gender}
                      setActiveFilter={(value) =>
                        handleUpdateLegacy(value, 'gender')
                      }
                      isDisabled={rowDisabled(i)}
                    />
                  </ItemContainer>
                  <ItemContainer>
                    <label>Tipo de entrenamiento</label>
                    <InlineMenu
                      menuName="routineType"
                      activeFilter={exer.routine}
                      setActiveFilter={(value) =>
                        handleUpdateLegacy(value, 'routine')
                      }
                      isDisabled={rowDisabled(i)}
                    />
                  </ItemContainer>
                </>
              ) : (
                <>
                  <ItemContainer>
                    <label>Categoría del ejercicio</label>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      {VOICE_CATEGORY_SLUGS.map((slug) => (
                        <Button
                          key={slug}
                          type="button"
                          className={`inline-menu ${voiceSlug === slug ? 'active' : ''}`}
                          disabled={rowDisabled(i)}
                          onClick={() => setVoiceCategoryAt(i, slug)}
                          label={VOICE_CATEGORY_LABELS[slug]}
                        />
                      ))}
                    </div>
                  </ItemContainer>
                  {(voiceSlug === 'chicas' || voiceSlug === 'chicos') && (
                    <ItemContainer>
                      <label>Tipo de entrenamiento</label>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 8,
                        }}
                      >
                        {TRAINING_SUBFOLDER_SLUGS.map((slug) => (
                          <Button
                            key={slug}
                            type="button"
                            className={`inline-menu ${exer.routine === slug ? 'active' : ''}`}
                            disabled={rowDisabled(i)}
                            onClick={() => setTrainingSubfolderAt(i, slug)}
                            label={TRAINING_TYPE_LABELS[slug]}
                          />
                        ))}
                      </div>
                    </ItemContainer>
                  )}
                </>
              )}

              {openIndex === i && activeList && activeList.length > 0 && (
                <ItemContainer>
                  <label>Selecciona el ejercicio</label>

                  <SelectComponent
                    value={exer.exercise?.title || ''}
                    onChange={handleChange}
                    name="exercise"
                    disabled={!onEditionMode}
                  >
                    <option value="">
                      {exer.exercise?.title
                        ? exer.exercise?.title.replace('.mp3', '')
                        : 'Seleccionar audio'}
                    </option>

                    {activeList.map(({ title, url }, idx) => (
                      <option key={idx} value={`${title} | ${url}`}>
                        {title.replace('.mp3', '')}
                      </option>
                    ))}
                  </SelectComponent>
                </ItemContainer>
              )}
              <ItemContainer>
                <label>Observaciones</label>
                <Textarea
                  name="observation"
                  id="observation"
                  onChange={handleChange}
                  placeholder="Agregar observación"
                  readOnly={!onEditionMode}
                  defaultValue={exer.observation || ''}
                />
              </ItemContainer>
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </AccordionContainer>
  )
}

export default ExerciseAccordion
